import { getClient, getActiveSpan, getRootSpan, spanToJSON, logger, browserPerformanceTimeOrigin, getCurrentScope, htmlTreeAsString, dropUndefinedKeys, SEMANTIC_ATTRIBUTE_SENTRY_ORIGIN, SEMANTIC_ATTRIBUTE_SENTRY_OP, SEMANTIC_ATTRIBUTE_EXCLUSIVE_TIME, SEMANTIC_ATTRIBUTE_SENTRY_MEASUREMENT_UNIT, SEMANTIC_ATTRIBUTE_SENTRY_MEASUREMENT_VALUE } from '@sentry/core';
import { DEBUG_BUILD } from '../debug-build.js';
import { addClsInstrumentationHandler } from './instrument.js';
import { msToSec, startStandaloneWebVitalSpan } from './utils.js';
import { onHidden } from './web-vitals/lib/onHidden.js';
/**
 * Starts tracking the Cumulative Layout Shift on the current page and collects the value once
 *
 * - the page visibility is hidden
 * - a navigation span is started (to stop CLS measurement for SPA soft navigations)
 *
 * Once either of these events triggers, the CLS value is sent as a standalone span and we stop
 * measuring CLS.
 */

function trackClsAsStandaloneSpan() {
  var standaloneCLsValue = 0;
  var standaloneClsEntry;
  var pageloadSpanId;

  if (!supportsLayoutShift()) {
    return;
  }

  var sentSpan = false;

  function _collectClsOnce() {
    if (sentSpan) {
      return;
    }

    sentSpan = true;

    if (pageloadSpanId) {
      sendStandaloneClsSpan(standaloneCLsValue, standaloneClsEntry, pageloadSpanId);
    }

    cleanupClsHandler();
  }

  var cleanupClsHandler = addClsInstrumentationHandler(_ref => {
    var {
      metric
    } = _ref;
    var entry = metric.entries[metric.entries.length - 1];

    if (!entry) {
      return;
    }

    standaloneCLsValue = metric.value;
    standaloneClsEntry = entry;
  }, true); // use pagehide event from web-vitals

  onHidden(() => {
    _collectClsOnce();
  }); // Since the call chain of this function is synchronous and evaluates before the SDK client is created,
  // we need to wait with subscribing to a client hook until the client is created. Therefore, we defer
  // to the next tick after the SDK setup.

  setTimeout(() => {
    var client = getClient();

    if (!client) {
      return;
    }

    var unsubscribeStartNavigation = client.on('startNavigationSpan', () => {
      _collectClsOnce();

      unsubscribeStartNavigation === null || unsubscribeStartNavigation === void 0 ? void 0 : unsubscribeStartNavigation();
    });
    var activeSpan = getActiveSpan();

    if (activeSpan) {
      var rootSpan = getRootSpan(activeSpan);
      var spanJSON = spanToJSON(rootSpan);

      if (spanJSON.op === 'pageload') {
        pageloadSpanId = rootSpan.spanContext().spanId;
      }
    }
  }, 0);
}

function sendStandaloneClsSpan(clsValue, entry, pageloadSpanId) {
  var _entry$sources$;

  DEBUG_BUILD && logger.log("Sending CLS span (".concat(clsValue, ")"));
  var startTime = msToSec((browserPerformanceTimeOrigin() || 0) + ((entry === null || entry === void 0 ? void 0 : entry.startTime) || 0));
  var routeName = getCurrentScope().getScopeData().transactionName;
  var name = entry ? htmlTreeAsString((_entry$sources$ = entry.sources[0]) === null || _entry$sources$ === void 0 ? void 0 : _entry$sources$.node) : 'Layout shift';
  var attributes = dropUndefinedKeys({
    [SEMANTIC_ATTRIBUTE_SENTRY_ORIGIN]: 'auto.http.browser.cls',
    [SEMANTIC_ATTRIBUTE_SENTRY_OP]: 'ui.webvital.cls',
    [SEMANTIC_ATTRIBUTE_EXCLUSIVE_TIME]: (entry === null || entry === void 0 ? void 0 : entry.duration) || 0,
    // attach the pageload span id to the CLS span so that we can link them in the UI
    'sentry.pageload.span_id': pageloadSpanId
  });
  var span = startStandaloneWebVitalSpan({
    name,
    transaction: routeName,
    attributes,
    startTime
  });

  if (span) {
    span.addEvent('cls', {
      [SEMANTIC_ATTRIBUTE_SENTRY_MEASUREMENT_UNIT]: '',
      [SEMANTIC_ATTRIBUTE_SENTRY_MEASUREMENT_VALUE]: clsValue
    }); // LayoutShift performance entries always have a duration of 0, so we don't need to add `entry.duration` here
    // see: https://developer.mozilla.org/en-US/docs/Web/API/PerformanceEntry/duration

    span.end(startTime);
  }
}

function supportsLayoutShift() {
  try {
    return PerformanceObserver.supportedEntryTypes.includes('layout-shift');
  } catch (_unused) {
    return false;
  }
}

export { trackClsAsStandaloneSpan };